import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeCaES from '@angular/common/locales/ca';
import localeEsES from '@angular/common/locales/es';
import localeFrFR from '@angular/common/locales/fr';
import localeFrBE from '@angular/common/locales/fr-BE';
import localeNlBE from '@angular/common/locales/nl-BE';
import {
  ApplicationConfig,
  DOCUMENT,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { BrowserModule, Meta, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { httpInterceptorProviders } from '@api/interceptors';
import { environment } from '@environments/environment';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { INDEXED_DB_CONFIG, IndexedDbService } from '@shared/services';
import { AUTH_CALLBACK_PATH, AuthenticationService } from '@shared/services/authentication';
import { CommonService } from '@shared/services/common';
import { LocalizedCommon } from '@shared/services/common/models';
import {
  CurrentLanguageService,
  CustomTranslateLoader,
  LANGUAGE_FALLBACK,
  LanguageAvailable,
} from '@shared/services/language';
import { GlobalErrorHandler, LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { RoutePopinDispatcherService } from '@shared/services/route-popin-dispatcher';
import { LocalizedCommonStore } from '@stores';
import { filter, firstValueFrom, map, of, switchMap, take, timeout } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { APP_ROUTES } from './app.routes';

const ANGULAR_LOCALE_DATA: Readonly<Record<LanguageAvailable, unknown>> = {
  'fr-FR': localeFrFR,
  'es-ES': localeEsES,
  'ca-ES': localeCaES,
  'fr-BE': localeFrBE,
  'nl-BE': localeNlBE,
};
for (const [locale, data] of Object.entries(ANGULAR_LOCALE_DATA)) {
  registerLocaleData(data, locale);
}

const ROBOTS_INDEX = 'index, follow';
const ROBOTS_NOINDEX = 'noindex, follow, nosnippet, noimageindex, notranslate';

function updateRobotsMeta(meta: Meta, content: string): void {
  meta.removeTag("name='robots'");
  meta.addTag({ name: 'robots', content });
}

function initializeMetaRobots(): void {
  const meta = inject(Meta);
  const robotsContent = environment.allowIndexing ? ROBOTS_INDEX : ROBOTS_NOINDEX;
  updateRobotsMeta(meta, robotsContent);
}

// An initializer, not a route: the header, footer and scripts render before any route is evaluated.
function handleAuthCallback(): Promise<never> | void {
  if (inject(PlatformContextService).isBrowser() && location.pathname === `/${AUTH_CALLBACK_PATH}`) {
    return inject(AuthenticationService).handleRedirectCallback();
  }
}

function initializeApp(): () => Promise<void> {
  return async (): Promise<void> => {
    const logsService = inject(LogsService);
    const indexedDbService = inject(IndexedDbService);
    const commonService = inject(CommonService);
    const localizedCommonStore = inject(LocalizedCommonStore);
    const logs = inject(LogsService);

    const load$ = commonService.readResource<LocalizedCommon>('common').pipe(
      take(1),
      timeout({ each: 5000 }),
      catchError((err: unknown) => {
        logs.error('initializeApp', 'Unable to load common data. This may affect core functionality.', { error: err });
        return of<LocalizedCommon | null>(null);
      })
    );

    logsService.updateLogLevelFromSession();

    try {
      await firstValueFrom(
        indexedDbService.connectToDB().pipe(
          filter((ok) => ok),
          take(1),
          switchMap(() => load$),
          tap((localizedCommon) => {
            if (localizedCommon) localizedCommonStore.set(localizedCommon);
          }),
          map(() => void 0)
        )
      );
    } catch (err) {
      logs.error(
        'initializeApp',
        'App initialization failed, continuing in degraded mode (IndexedDB/common data unavailable)',
        err
      );
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideTranslateService({
      fallbackLang: LANGUAGE_FALLBACK,
      // Not provideTranslateLoader(): ngx-translate 18 tells a class by `/^class\s/` on its source, which the
      // minified `class{` of an optimized build fails, and then calls it as a factory, without `new`.
      loader: { provide: TranslateLoader, useClass: CustomTranslateLoader },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideClientHydration(withEventReplay()),
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'disabled',
      }),
      withComponentInputBinding()
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Zoneless: without these listeners, window 'error' and 'unhandledrejection' events never reach the ErrorHandler
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    { provide: LOCALE_ID, useFactory: () => inject(CurrentLanguageService).language.locale },
    provideAppInitializer(handleAuthCallback),
    // Resolves the language before initializeApp sends the first request (DIGSOC-5191, decision 2).
    provideAppInitializer(() => {
      inject(DOCUMENT).documentElement.lang = inject(CurrentLanguageService).language.locale;
    }),
    provideAppInitializer(initializeMetaRobots),
    provideAppInitializer(() => {
      const isServer = inject(PlatformContextService).isServer();
      if (!isServer) inject(RoutePopinDispatcherService).initialize();
    }),
    provideAppInitializer(() => initializeApp()()),
    {
      provide: INDEXED_DB_CONFIG,
      useValue: ['socooc', 1], // [IndexDB database name, Database version]
    },
    ...httpInterceptorProviders,
  ],
};
